import React from 'react';
import { motion } from 'framer-motion';
import OrderTile from '../OrderTile';
import { Skeleton } from '../Skeleton';
import { mapBackendStatusToFrontend } from '../../services/api';
import { StatusType } from '../StatusTag';

interface OrderSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  orders: any[];
  isLoading?: boolean;
  onOrderClick?: (orderId: string) => void;
}

export const OrderSidePanel: React.FC<OrderSidePanelProps> = ({
  isOpen,
  onClose,
  orders,
  isLoading = false,
  onOrderClick
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Bottom Sheet Content */}
      <motion.div
        className="bg-background rounded-t-3xl z-10 w-full relative flex flex-col"
        style={{ height: '80vh' }}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar */}
        <div className="flex items-center justify-center pt-3 pb-2 shrink-0">
          <div className="bg-[rgba(0,0,0,0.12)] rounded-full w-10 h-1" />
        </div>

        {/* Header */}
        <div className="px-4 py-3 shrink-0 border-b border-border">
          <h2 className="font-sans font-semibold text-lg text-text tracking-[-0.16px]">
            Pedidos del cliente
          </h2>
        </div>

        {/* Orders List */}
        <div className="flex-1 flex flex-col relative w-full min-h-0 overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col gap-2 px-4 py-3">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="px-2 py-3">
                  <Skeleton className="h-20 w-full rounded-lg" />
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <p className="font-sans font-medium text-base text-text-subtle text-center">
                No hay pedidos registrados para este cliente
              </p>
            </div>
          ) : (
            <div className="flex-1 flex py-2 relative w-full min-h-0 overflow-y-auto">
              <div className="flex-1 bg-background flex flex-col items-start p-2 rounded-lg w-full">
                {/* Desktop Header */}
                {!isLoading && orders.length > 0 && (
                  <div
                    className="hidden md:flex items-center px-0 py-2 pb-3 border-b border-border w-full"
                    style={{ gap: '16px' }}
                  >
                    <div className="w-4" />
                    <div className="w-[120px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Pedido
                      </p>
                    </div>
                    <div className="w-[250px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Cliente
                      </p>
                    </div>
                    <div className="w-[150px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Teléfono
                      </p>
                    </div>
                    <div className="w-[120px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Productos
                      </p>
                    </div>
                    <div className="w-[120px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Total
                      </p>
                    </div>
                    <div className="w-[140px]">
                      <p className="font-sans font-semibold text-xs text-text-subtle uppercase tracking-wide">
                        Estado
                      </p>
                    </div>
                    <div className="flex-1" />
                  </div>
                )}

                {/* Order Tiles */}
                <div className="w-full p-2">
                  {orders.map((order) => {
                    const customerName = order.customerName || order.customer_name || '';
                    const customerPhone = order.customerPhone || order.customer_phone || '';
                    const formattedPhone = customerPhone ? `+${customerPhone}` : '';
                    const totalAmount = parseFloat(
                      order.totalAmount?.toString() || order.total_amount?.toString() || '0'
                    );

                    return (
                      <OrderTile
                        key={order.id}
                        orderId={`P-${order.id}`}
                        company={customerName || 'Cliente no registrado'}
                        customer={formattedPhone}
                        productCount={order.items?.length || 0}
                        total={`$${totalAmount.toLocaleString('es-CL')}`}
                        status={mapBackendStatusToFrontend(order.status) as StatusType}
                        hasUnread={false}
                        onClick={() => {
                          onClose();
                          if (onOrderClick) {
                            onOrderClick(`P-${order.id}`);
                          }
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OrderSidePanel;
